function uniqueSkuIds(ids) {
    const seen = new Set();
    const out = [];
    for (const id of ids || []) {
        if (id == null || seen.has(id)) continue;
        seen.add(id);
        out.push(id);
    }
    return out;
}

function sortToOriginal(ids, originalIds) {
    if (!originalIds?.length) return ids;
    return [...ids].sort((a, b) => {
        const ai = originalIds.indexOf(a);
        const bi = originalIds.indexOf(b);
        return (ai < 0 ? Number.MAX_SAFE_INTEGER : ai) - (bi < 0 ? Number.MAX_SAFE_INTEGER : bi);
    });
}

export function assignGroupToBay(bays, planning, { bayId, groupId, count }) {
    if (!bayId || !groupId || !count) {
        return { bays, planning };
    }

    const planningIndex = planning.findIndex(d => d.id == groupId);
    const bayIndex = bays.findIndex(d => d.id == bayId);
    if (planningIndex < 0 || bayIndex < 0) {
        return { bays, planning };
    }

    const source = planning[planningIndex];
    const take = Math.min(Number(count) || 0, source.data?.length || 0);
    if (take <= 0) {
        return { bays, planning };
    }

    const dataExtracted = source.data.slice(0, take);
    const dataRemaining = source.data.slice(take);

    const nextPlanning =
        dataRemaining.length === 0
            ? planning.filter((_, i) => i !== planningIndex)
            : planning.map((item, i) => (i === planningIndex ? { ...item, data: dataRemaining } : item));

    const nextBays = bays.map((bay, i) => {
        if (i !== bayIndex) return bay;
        const existingIdx = bay.assigned.findIndex(d => d.id == groupId);
        const assigned =
            existingIdx > -1
                ? bay.assigned.map((item, j) =>
                    j === existingIdx ? { ...item, data: [...item.data, ...dataExtracted] } : item
                )
                : [...bay.assigned, { id: source.id, group: source.group, data: dataExtracted }];
        return { ...bay, assigned };
    });

    return { bays: nextBays, planning: nextPlanning };
}

export function assignGroupsToBay(bays, planning, { bayId, groupIds }) {
    const ids = uniqueSkuIds(groupIds);
    let next = { bays, planning };
    for (const groupId of ids) {
        const item = next.planning.find(d => d.id == groupId);
        const count = item?.data?.length ?? 0;
        next = assignGroupToBay(next.bays, next.planning, { bayId, groupId, count });
    }
    return next;
}

export function removeGroupFromBay(bays, planning, originalPlanning, { bayId, groupId }) {
    const bayIndex = bays.findIndex(d => d.id == bayId);
    if (bayIndex < 0) {
        return { bays, planning };
    }

    const assignedIndex = bays[bayIndex].assigned.findIndex(d => d.id == groupId);
    if (assignedIndex < 0) {
        return { bays, planning };
    }

    const removed = bays[bayIndex].assigned[assignedIndex];
    const removedData = removed.data || [];
    const nextBays = bays.map((bay, i) => {
        if (i !== bayIndex) return bay;
        return { ...bay, assigned: bay.assigned.filter((_, j) => j !== assignedIndex) };
    });

    const planningIndex = planning.findIndex(d => d.id == groupId);
    const original = originalPlanning.find(d => d.id == groupId);

    if (planningIndex > -1) {
        const merged = sortToOriginal(uniqueSkuIds([...planning[planningIndex].data, ...removedData]), original?.data);
        return {
            bays: nextBays,
            planning: planning.map((item, i) => (i === planningIndex ? { ...item, data: merged } : item)),
        };
    }

    const insertAtRaw = originalPlanning.findIndex(d => d.id == groupId);
    const insertAt = insertAtRaw < 0 ? planning.length : Math.min(insertAtRaw, planning.length);
    const restored = { id: removed.id, group: removed.group, data: [...removedData] };
    return {
        bays: nextBays,
        planning: [...planning.slice(0, insertAt), restored, ...planning.slice(insertAt)],
    };
}

export function clearBay(bays, planning, originalPlanning, bayId) {
    const bay = bays.find(d => d.id == bayId);
    if (!bay?.assigned?.length) {
        return { bays, planning };
    }
    const groupIds = bay.assigned.map(item => item.id);
    let next = { bays, planning };
    for (const groupId of groupIds) {
        next = removeGroupFromBay(next.bays, next.planning, originalPlanning, { bayId, groupId });
    }
    return next;
}

export function pendencyCount(planning) {
    return (planning || []).reduce((total, item) => total + (item?.data?.length || 0), 0);
}

export const PLANNING_GROUP_DRAG = 'application/x-planning-group';

export function setGroupDragData(event, groupIds) {
    const ids = Array.isArray(groupIds) ? groupIds.filter(Boolean) : [groupIds].filter(Boolean);
    const payload = JSON.stringify(ids);
    event.dataTransfer.setData(PLANNING_GROUP_DRAG, payload);
    event.dataTransfer.setData('text/plain', payload);
    event.dataTransfer.effectAllowed = 'move';
}

export function getGroupDragIds(event) {
    const raw = event.dataTransfer.getData(PLANNING_GROUP_DRAG) || event.dataTransfer.getData('text/plain');
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [parsed];
    } catch {
        return [raw];
    }
}
